import BigImport from 'big.js';
import type { Big as BigInstance, BigConstructor } from 'big.js';

/**
 * Decimal arithmetic for financial values.
 *
 * All money, quantity and rate calculations in easiibill go through this module so that
 * the browser and the Worker produce byte-identical results. A private Big constructor is
 * used so global configuration of big.js can never be changed by other code.
 */
const DecimalCtor: BigConstructor = (BigImport as unknown as BigConstructor)();
DecimalCtor.DP = 24;
DecimalCtor.RM = 1;
DecimalCtor.NE = -40;
DecimalCtor.PE = 40;

export type Decimal = BigInstance;
export type DecimalLike = Decimal | string | number;

export type RoundingMode = 'half_up' | 'half_even' | 'up' | 'down';

const ROUNDING: Record<RoundingMode, 0 | 1 | 2 | 3> = {
  down: 0,
  half_up: 1,
  half_even: 2,
  up: 3,
};

const DECIMAL_PATTERN = /^[-+]?(?:\d+\.?\d*|\.\d+)$/;

export class DecimalError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DecimalError';
  }
}

function isDecimal(value: unknown): value is Decimal {
  return (
    typeof value === 'object' &&
    value !== null &&
    'c' in value &&
    'e' in value &&
    's' in value &&
    typeof (value as { toFixed?: unknown }).toFixed === 'function'
  );
}

/** Converts a value to a Decimal. Empty values become zero; malformed values throw. */
export function d(value: DecimalLike | null | undefined): Decimal {
  if (value === null || value === undefined) return new DecimalCtor(0);
  if (isDecimal(value)) return new DecimalCtor(value);
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new DecimalError(`Invalid number: ${value}`);
    return new DecimalCtor(value);
  }
  const trimmed = value.trim();
  if (trimmed === '') return new DecimalCtor(0);
  if (!DECIMAL_PATTERN.test(trimmed)) throw new DecimalError(`Invalid decimal value: "${value}"`);
  return new DecimalCtor(trimmed.startsWith('+') ? trimmed.slice(1) : trimmed);
}

/** Parses a decimal string, returning null instead of throwing. */
export function parseDecimal(value: unknown): Decimal | null {
  if (typeof value === 'number') return Number.isFinite(value) ? new DecimalCtor(value) : null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed === '' || !DECIMAL_PATTERN.test(trimmed)) return null;
  return new DecimalCtor(trimmed.startsWith('+') ? trimmed.slice(1) : trimmed);
}

/** Returns true when the string is a plain decimal with at most `maxDecimals` fraction digits. */
export function isDecimalString(value: string, maxDecimals = 10): boolean {
  const trimmed = value.trim();
  if (!DECIMAL_PATTERN.test(trimmed)) return false;
  const fraction = trimmed.split('.')[1];
  return fraction === undefined || fraction.length <= maxDecimals;
}

export const ZERO: Decimal = new DecimalCtor(0);
export const ONE: Decimal = new DecimalCtor(1);
export const HUNDRED: Decimal = new DecimalCtor(100);

export function round(value: DecimalLike, decimals: number, mode: RoundingMode = 'half_up'): Decimal {
  return d(value).round(decimals, ROUNDING[mode]);
}

/** Rounds to the nearest multiple of `increment` (e.g. 0.05, 1, 10). */
export function roundToIncrement(
  value: DecimalLike,
  increment: DecimalLike,
  mode: RoundingMode = 'half_up',
): Decimal {
  const inc = d(increment);
  if (inc.lte(0)) return d(value);
  return d(value).div(inc).round(0, ROUNDING[mode]).times(inc);
}

export function sum(values: readonly DecimalLike[]): Decimal {
  let total = new DecimalCtor(0);
  for (const value of values) total = total.plus(d(value));
  return total;
}

export function max(a: DecimalLike, b: DecimalLike): Decimal {
  const x = d(a);
  const y = d(b);
  return x.gte(y) ? x : y;
}

export function min(a: DecimalLike, b: DecimalLike): Decimal {
  const x = d(a);
  const y = d(b);
  return x.lte(y) ? x : y;
}

export function clamp(value: DecimalLike, lower: DecimalLike, upper: DecimalLike): Decimal {
  return min(max(value, lower), upper);
}

export function isZero(value: DecimalLike): boolean {
  return d(value).eq(0);
}

/** Fixed-point string with exactly `decimals` fraction digits. */
export function toFixed(value: DecimalLike, decimals: number, mode: RoundingMode = 'half_up'): string {
  return round(value, decimals, mode).toFixed(decimals);
}

/** Plain string without trailing zeros (e.g. "1.5", "12"). */
export function toPlain(value: DecimalLike, maxDecimals = 10): string {
  const fixed = round(value, maxDecimals).toFixed(maxDecimals);
  if (!fixed.includes('.')) return fixed;
  const trimmed = fixed.replace(/0+$/, '').replace(/\.$/, '');
  return trimmed === '-0' ? '0' : trimmed;
}

/** Converts a decimal amount to integer minor units (e.g. 12.34 USD → 1234). */
export function toMinor(value: DecimalLike, decimals: number): number {
  const scaled = round(value, decimals).times(new DecimalCtor(10).pow(decimals));
  const result = Number(scaled.toFixed(0));
  if (!Number.isSafeInteger(result)) throw new DecimalError('Amount exceeds the supported range');
  return result;
}

/** Converts integer minor units back to a fixed decimal string. */
export function fromMinor(minor: number, decimals: number): string {
  if (!Number.isFinite(minor)) throw new DecimalError(`Invalid minor amount: ${minor}`);
  return new DecimalCtor(Math.trunc(minor)).div(new DecimalCtor(10).pow(decimals)).toFixed(decimals);
}

/**
 * Splits `total` across `weights` proportionally, rounded to `decimals`, using the largest
 * remainder method so that the parts always add up exactly to the rounded total.
 */
export function allocate(total: DecimalLike, weights: readonly DecimalLike[], decimals: number): Decimal[] {
  const factor = new DecimalCtor(10).pow(decimals);
  const totalUnits = round(total, decimals).times(factor);
  const weightValues = weights.map((w) => max(d(w), 0));
  const weightSum = sum(weightValues);
  if (weightValues.length === 0) return [];
  if (weightSum.eq(0) || totalUnits.eq(0)) return weightValues.map(() => new DecimalCtor(0));

  const negative = totalUnits.lt(0);
  const absUnits = totalUnits.abs();
  const raw = weightValues.map((w) => absUnits.times(w).div(weightSum));
  const floors = raw.map((r) => r.round(0, 0));
  let remaining = Number(absUnits.minus(sum(floors)).toFixed(0));
  const order = raw
    .map((r, index) => ({ index, remainder: r.minus(floors[index] ?? 0) }))
    .sort((a, b) => b.remainder.cmp(a.remainder) || a.index - b.index);
  const units = floors.slice();
  for (const entry of order) {
    if (remaining <= 0) break;
    units[entry.index] = (units[entry.index] ?? new DecimalCtor(0)).plus(1);
    remaining -= 1;
  }
  return units.map((u) => {
    const value = u.div(factor);
    return negative ? value.times(-1) : value;
  });
}

export function percentOf(value: DecimalLike, percent: DecimalLike): Decimal {
  return d(value).times(d(percent)).div(100);
}
